import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

/**
 *  KRÉTA CSOPORTBESOROLÁS – szinkron, párosítás, átvétel
 *
 *  Ez váltja ki a kézi kört: a KRÉTA besorolás-képernyőjéről letöltött Excelt eddig
 *  a csoporttagság-importba kellett betölteni. Itt ugyanaz történik, csak fájl nélkül.
 *
 *  A KÉPERNYŐ HÁROM LÉPÉS, ÉS A KÖZÉPSŐ A LÉNYEG:
 *    1. Szinkron – letölti a KRÉTA besorolásait (nem ír a névsorba semmit).
 *    2. Párosítás – melyik KRÉTA-csoport melyik SULINFORM-csoport. A névegyezés
 *       magától megvan; ami nem egyértelmű, azt itt lehet kézzel megmondani.
 *    3. Átvétel – az előnézet megmutatja, ki kerülne be és ki esne ki, és csak a
 *       jóváhagyás után ír.
 *
 *  A LEZÁRÁS KÜLÖN KAPCSOLÓ, mert névsort vesz el. Ha egy csoportnál akár egyetlen
 *  tanuló sem azonosítható, ott a kiszolgáló magától nem zár le semmit.
 */
@Component({
  selector: 'app-admin-kreta-csoport',
  templateUrl: './kreta-csoport.component.html',
  styleUrls: ['./kreta-csoport.component.scss'],
})
export class AdminKretaCsoportComponent implements OnInit, OnDestroy {
  sajatIntezmeny: any;

  allapot: any = null;
  fiok = '';
  folyamat: any = null;
  private figyelo: any = null;

  parok: any[] = [];
  sajatCsoportok: any[] = [];

  terv: any = null;
  lezaras = false;

  osztalyElteres: any = null;
  visszairas: any = null;

  toltodik = false;
  fut = false;
  hiba = '';

  /* ── Tanulói törzsadatok (külön modul: /api/kreta-tanulo-adat) ── */
  td: any = null;                 // állapot
  tdFolyamat: any = null;
  tdTerv: any = null;             // előnézet
  tdMezok: any = null;            // látott KRÉTA-mezők (ellenőrzéshez)
  tdValtozasok: any = null;       // a legutóbbi átvétel naplója
  tdFut = false;
  private tdFigyelo: any = null;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    await this.allapotBetolt();
    await this.parositasBetolt();
    await this.tdAllapotBetolt();
  }

  ngOnDestroy() { this.figyeloLeall(); this.tdFigyeloLeall(); }

  private get int() { return String(this.sajatIntezmeny?.intezmenyId || '').replace(/^\/+|\/+$/g, ''); }

  async allapotBetolt() {
    try {
      this.allapot = await firstValueFrom(this.http.get<any>(`/api/kreta-csoport/allapot/${this.int}`));
      this.folyamat = this.allapot?.folyamat || null;
      if (!this.fiok) this.fiok = String(this.allapot?.fiokok?.[0]?.allomany || '');
      if (this.folyamat?.fut) this.figyeloIndit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Az állapot betöltése nem sikerült.'; }
  }

  async parositasBetolt() {
    this.toltodik = true;
    try {
      const r: any = await firstValueFrom(this.http.get<any>(`/api/kreta-csoport/parositas/${this.int}`));
      this.parok = r?.tetelek || [];
      //  A kézi választáshoz a TELJES saját csoportlista kell, nem csak a névegyezők.
      this.sajatCsoportok = await firstValueFrom(
        this.http.get<any[]>(`/api/kreta-csoport/celok/${this.int}?fajta=csoport`)).catch(() => []);
    } catch (e: any) { this.hiba = e?.error?.error || 'A párosítás betöltése nem sikerült.'; }
    this.toltodik = false;
  }

  /* ── Szinkron ── */

  async szinkron() {
    if (!this.fiok) { this.data.sendToast('Válaszd ki, melyik KRÉTA-fiókkal induljon.', 'warning'); return; }
    this.fut = true; this.hiba = '';
    try {
      await firstValueFrom(this.http.post(`/api/kreta-csoport/szinkron/${this.int}`, { fiok: this.fiok }));
      this.data.sendToast('A szinkron elindult – a KRÉTA-belépés és a letöltés percekig tarthat.', 'success');
      this.figyeloIndit();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A szinkron nem indult el.', 'error');
      this.fut = false;
    }
  }

  /*  A letöltés a háttérben fut, ezért kérdezni kell: enélkül a képernyő nem mondaná
   *  meg, hol tart, és a felhasználó azt hinné, semmi nem történik. */
  private figyeloIndit() {
    this.figyeloLeall();
    this.fut = true;
    this.figyelo = setInterval(async () => {
      try {
        const f: any = await firstValueFrom(this.http.get<any>(`/api/kreta-csoport/folyamat/${this.int}`));
        this.folyamat = f;
        if (!f?.fut) {
          this.figyeloLeall();
          this.fut = false;
          if (f?.hiba) this.data.sendToast(f.hiba, 'error');
          else this.data.sendToast('A szinkron kész.', 'success');
          await this.allapotBetolt();
          await this.parositasBetolt();
        }
      } catch { /* egy kimaradt kérdezés nem baj, a következő újra megpróbálja */ }
    }, 4000);
  }

  private figyeloLeall() { if (this.figyelo) { clearInterval(this.figyelo); this.figyelo = null; } }

  /* ── Párosítás ── */

  async parositasMent(p: any) {
    try {
      await firstValueFrom(this.http.put(`/api/kreta-csoport/parositas/${this.int}`,
        { fajta: 'csoport', kreta_id: p.kreta_id, cel: p.cel || null }));
      this.data.sendToast('Párosítás mentve.', 'success');
      await this.parositasBetolt();
      this.terv = null;
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'A párosítás mentése nem sikerült.', 'error'); }
  }

  get parositatlanDb(): number { return this.parok.filter((p) => !p.cel).length; }
  get azonositatlanDb(): number { return this.parok.reduce((s, p) => s + (Number(p.azonositatlan_db) || 0), 0); }

  /* ── Előnézet és átvétel ── */

  async elonezet() {
    this.toltodik = true; this.terv = null;
    try {
      this.terv = await firstValueFrom(this.http.get<any>(
        `/api/kreta-csoport/elonezet/${this.int}?lezaras=${this.lezaras ? 1 : 0}`));
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'Az előnézet nem sikerült.', 'error'); }
    this.toltodik = false;
  }

  async atvesz() {
    if (!this.terv) return;
    this.fut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(`/api/kreta-csoport/atvetel/${this.int}`,
        { lezaras: this.lezaras }));
      this.data.sendToast(`${r?.uj ?? 0} új tagság, ${r?.lezart ?? 0} lezárva`
        + (r?.csoport_uj ? `, ${r.csoport_uj} új csoport a KRÉTÁ-ból` : '') + '.', 'success');
      await this.elonezet();
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'Az átvétel nem sikerült.', 'error'); }
    this.fut = false;
  }

  /* ── Osztályeltérés és visszaírás ── */

  async osztalyBetolt() {
    this.toltodik = true;
    try {
      this.osztalyElteres = await firstValueFrom(this.http.get<any>(`/api/kreta-csoport/osztaly-elteres/${this.int}`));
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'Az osztályeltérés nem sikerült.', 'error'); }
    this.toltodik = false;
  }

  async visszairasBetolt() {
    this.toltodik = true;
    try {
      this.visszairas = await firstValueFrom(this.http.get<any>(`/api/kreta-csoport/visszairandok/${this.int}`));
      this.visszairasAuto = !!this.visszairas?.elesitve;
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'A visszaírandók listája nem sikerült.', 'error'); }
    this.toltodik = false;
  }

  visszairasAuto = false;
  vFut = false;
  vEredmeny: any = null;

  async visszairasAutoMent() {
    try {
      await firstValueFrom(this.http.put(`/api/kreta-csoport/visszairas-beallitas/${this.int}`, { visszairas_auto: this.visszairasAuto }));
      this.data.sendToast(this.visszairasAuto ? 'A visszaírás bekapcsolva.' : 'A visszaírás kikapcsolva.', 'success');
      await this.visszairasBetolt();
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error'); }
  }

  /** `szaraz`: belép a KRÉTÁ-ba és kitölti a lapokat, de nem ment. */
  async visszairasKuld(szaraz: boolean) {
    if (!szaraz && !confirm('A küldhető tételeket átvezeted a KRÉTÁ-ban? A rendszer a KRÉTA besorolás-, illetve kiléptetés-lapján menti őket.')) return;
    this.vFut = true;
    try {
      this.vEredmeny = await firstValueFrom(this.http.post<any>(`/api/kreta-csoport/visszairas/${this.int}`, { szaraz }));
      await this.visszairasBetolt();
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'A visszaírás nem sikerült.', 'error'); }
    this.vFut = false;
  }

  /* ── Tanulói törzsadatok a KRÉTÁ-ból ── */

  async tdAllapotBetolt() {
    try {
      this.td = await firstValueFrom(this.http.get<any>(`/api/kreta-tanulo-adat/allapot/${this.int}`));
      this.tdFolyamat = this.td?.folyamat || null;
      if (this.tdFolyamat?.fut) this.tdFigyeloIndit();
    } catch { /* a modul hiánya nem buktatja a képernyőt */ }
  }

  get tdUtolsoLetoltes(): any {
    return (this.td?.futasok || []).find((f: any) => f.fajta === 'letoltes') || null;
  }

  async tdSzinkron() {
    if (!this.fiok) { this.data.sendToast('Válaszd ki fent, melyik KRÉTA-fiókkal induljon.', 'warning'); return; }
    this.tdFut = true;
    try {
      await firstValueFrom(this.http.post(`/api/kreta-tanulo-adat/szinkron/${this.int}`, { fiok: this.fiok }));
      this.data.sendToast('A törzsadat-letöltés elindult – tanulónként egy adatlap, ez percekig tart.', 'success');
      this.tdFigyeloIndit();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A letöltés nem indult el.', 'error');
      this.tdFut = false;
    }
  }

  private tdFigyeloIndit() {
    this.tdFigyeloLeall();
    this.tdFut = true;
    this.tdFigyelo = setInterval(async () => {
      try {
        const f: any = await firstValueFrom(this.http.get<any>(`/api/kreta-tanulo-adat/folyamat/${this.int}`));
        this.tdFolyamat = f;
        if (!f?.fut) {
          this.tdFigyeloLeall();
          this.tdFut = false;
          if (f?.hiba) this.data.sendToast(f.hiba, 'error');
          else this.data.sendToast('A törzsadat-letöltés kész – nézd meg az előnézetet.', 'success');
          await this.tdAllapotBetolt();
          await this.tdElonezet();
        }
      } catch { /* a következő kérdezés újrapróbálja */ }
    }, 4000);
  }

  private tdFigyeloLeall() { if (this.tdFigyelo) { clearInterval(this.tdFigyelo); this.tdFigyelo = null; } }

  async tdElonezet() {
    this.toltodik = true;
    try {
      this.tdTerv = await firstValueFrom(this.http.get<any>(`/api/kreta-tanulo-adat/elonezet/${this.int}`));
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'Az előnézet nem sikerült.', 'error'); }
    this.toltodik = false;
  }

  /** A változások mezőnként összesítve – a teljes lista több ezer sor is lehet. */
  get tdOsszesito(): { nev: string; db: number }[] {
    const m = new Map<string, number>();
    for (const v of this.tdTerv?.valtozasok || []) m.set(v.mezo_nev, (m.get(v.mezo_nev) || 0) + 1);
    return [...m.entries()].map(([nev, db]) => ({ nev, db }));
  }

  async tdAtvesz() {
    const db = this.tdTerv?.valtozasok?.length || 0;
    //  Új tanulók (a KRÉTÁ-ban osztályba sorolt, nálunk nem szereplő diákok) – a fék felett nem.
    const uj = (this.tdTerv?.uj_tanulok?.length || 0) <= (this.tdTerv?.uj_tanulo_max || 0) ? (this.tdTerv?.uj_tanulok?.length || 0) : 0;
    if (!db && !uj) return;
    const reszek = [uj ? `${uj} új tanulót felveszünk (tanuló + jogviszony a KRÉTA szerinti osztályba)` : '',
      db ? `${db} mezőt átírunk a KRÉTA szerint (csak ahol a KRÉTÁ-ban van érték)` : ''].filter(Boolean);
    if (!confirm(`${reszek.join(', és ')}. Minden változás naplózva lesz. Folytatod?`)) return;
    this.tdFut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(`/api/kreta-tanulo-adat/atvetel/${this.int}`, {}));
      const ujDb = r?.uj_tanulok?.length || 0;
      this.data.sendToast((ujDb ? `${ujDb} új tanuló felvéve, ` : '') + `${r?.irt ?? 0} mező átvéve`
        + (r?.hibak?.length ? `, ${r.hibak.length} hiba.` : '.'), r?.hibak?.length ? 'warning' : 'success');
      await this.tdElonezet();
      await this.tdValtozasokBetolt();
      await this.tdAllapotBetolt();
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'Az átvétel nem sikerült.', 'error'); }
    this.tdFut = false;
  }

  async tdMezokBetolt() {
    this.toltodik = true;
    try {
      this.tdMezok = await firstValueFrom(this.http.get<any>(`/api/kreta-tanulo-adat/mezok/${this.int}`));
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'A mezőlista nem sikerült.', 'error'); }
    this.toltodik = false;
  }

  async tdValtozasokBetolt() {
    try {
      this.tdValtozasok = await firstValueFrom(this.http.get<any>(`/api/kreta-tanulo-adat/valtozasok/${this.int}`));
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'A változásnapló nem sikerült.', 'error'); }
  }

  nevsor(lista: any[]): string {
    return (lista || []).slice(0, 40).join(', ') + ((lista || []).length > 40 ? ' …' : '');
  }

  /** A visszaírandó lista tanulói objektumok – a névsor a nevet (vagy az azonosítót) kéri. */
  mapNev(tanulok: any[]): string[] {
    return (tanulok || []).map((t) => String(t?.nev || t?.oktazon || ''));
  }
}
